(() => {
  const parseJSON = (element) => {
    const script = element.querySelector('[data-bookverse-editions-config]');

    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return null;
    }
  };

  const escapeHTML = (value) => {
    const span = document.createElement('span');
    span.textContent = value || '';
    return span.innerHTML;
  };

  class ProductFormatPicker extends HTMLElement {
    connectedCallback() {
      this.config = parseJSON(this);
      this.values = this.querySelector('.product-layout__format-values');
      this.editionOptions = this.querySelector('[data-bookverse-format-options]');
      this.panels = Array.from(this.querySelectorAll('[data-product-format-panel]'));
      this.buyingColumn = this.closest('.product-layout__buying-column, .product-layout__info-column');

      this.bindFallbackButtons();

      if (this.buttons.length > 0) {
        this.selectFormat(this.dataset.defaultFormat || 'print');
      }

      if (this.config?.workId) {
        this.loadRelatedEditions();
      }
    }

    bindFallbackButtons() {
      this.buttons = Array.from(this.querySelectorAll('[data-product-format-option]'));

      this.buttons.forEach((button) => {
        if (button.dataset.productFormatBound === 'true') return;
        button.dataset.productFormatBound = 'true';
        button.addEventListener('click', () => this.selectFormat(button.dataset.productFormatOption));
      });
    }

    selectFormat(format) {
      const isPrint = format === 'print';

      this.buttons.forEach((button) => {
        const isSelected = button.dataset.productFormatOption === format;

        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-pressed', String(isSelected));
      });

      this.panels.forEach((panel) => {
        panel.hidden = panel.dataset.productFormatPanel !== format;
      });

      this.toggleShopifyBuyingFlow(isPrint);
    }

    toggleShopifyBuyingFlow(shouldShow) {
      if (!this.buyingColumn) return;

      Array.from(this.buyingColumn.children).forEach((element) => {
        if (element === this || element.contains(this)) return;

        const isShopifyBuyingFlow =
          element.matches('variant-picker, product-buy-buttons, product-pickup-availability, .column-layout') ||
          element.querySelector(
            'variant-picker, product-buy-buttons, product-pickup-availability, .column-layout, [data-product-price], product-quantity-selector'
          );

        if (isShopifyBuyingFlow) {
          element.hidden = !shouldShow;
        }
      });
    }

    async loadRelatedEditions() {
      if (!window.BookverseEditions || !this.editionOptions) return;

      const fetchedProducts = await window.BookverseEditions.fetchEditions(this.config);
      const products = window.BookverseEditions.filterProductsByWorkId(
        window.BookverseEditions.dedupeProducts([this.config.currentProduct, ...fetchedProducts])
          .map((product) => window.BookverseEditions.normalizeProduct(product, this.config)),
        this.config.workId
      );
      const hasRelatedProduct = products.some((product) => String(product.id) !== String(this.config.currentProduct.id));

      if (!hasRelatedProduct) {
        return;
      }

      this.renderEditionOptions(products);
      this.hidePrintFallbackButton();
      this.hidden = false;
      this.classList.add('product-layout__format-picker--editions');
      this.panels.forEach((panel) => {
        panel.hidden = true;
      });
      this.toggleShopifyBuyingFlow(true);
    }

    renderEditionOptions(products) {
      const groups = this.groupProductsByFormat(products);
      const options = groups.map((group) => this.renderEditionOption(group)).join('');

      this.editionOptions.innerHTML = options;
      this.editionOptions.hidden = false;
      this.bindFallbackButtons();
    }

    hidePrintFallbackButton() {
      const printFallbackButton = this.querySelector(
        '.product-layout__format-value[data-product-format-option="print"]'
      );

      if (printFallbackButton) {
        printFallbackButton.hidden = true;
      }
    }

    groupProductsByFormat(products) {
      const groupsByFormat = new Map();

      products.forEach((product) => {
        const key = product.format || this.config.labels.defaultFormat;
        const existing = groupsByFormat.get(key) || [];
        groupsByFormat.set(key, [...existing, product]);
      });

      return Array.from(groupsByFormat.entries())
        .map(([format, groupProducts]) => {
          const current = groupProducts.find((product) => String(product.id) === String(this.config.currentProduct.id));
          const availableProducts = groupProducts.filter((product) => product.available);
          const candidates = availableProducts.length > 0 ? availableProducts : groupProducts;
          const selectedProduct = current || [...candidates].sort((a, b) => a.price - b.price)[0];
          const lowestProduct = [...candidates].sort((a, b) => a.price - b.price)[0] || selectedProduct;

          return {
            format,
            products: groupProducts,
            selectedProduct,
            lowestProduct,
          };
        })
        .sort((a, b) => {
          const aCurrent = a.products.some((product) => String(product.id) === String(this.config.currentProduct.id));
          const bCurrent = b.products.some((product) => String(product.id) === String(this.config.currentProduct.id));

          if (aCurrent !== bCurrent) return aCurrent ? -1 : 1;

          return a.format.localeCompare(b.format);
        });
    }

    renderEditionOption(group) {
      const product = group.selectedProduct;
      const isCurrent = String(product.id) === String(this.config.currentProduct.id);
      const tag = isCurrent ? 'button' : 'a';
      const attrs = isCurrent
        ? `type="button" data-product-format-option="print" aria-label="${escapeHTML(
            `${product.editionName || product.format}, ${product.priceText}, ${this.config.labels.current}`
          )}" aria-pressed="true"`
        : `href="${escapeHTML(product.url)}"`;
      const stateClasses = [
        'button',
        'button--secondary',
        'product-layout__choice-value',
        'product-layout__format-edition',
        isCurrent ? 'is-selected' : '',
        !product.available ? 'is-sold-out' : '',
      ]
        .filter(Boolean)
        .join(' ');
      const primaryLabel = product.editionName || product.format;
      const secondaryLabel = product.editionName && product.editionName !== product.format ? product.format : '';
      const hasMoreEditions = group.products.length > 1;
      const pricePrefix = hasMoreEditions && !isCurrent ? `${this.config.labels.from} ` : '';
      const priceText = `${pricePrefix}${hasMoreEditions && !isCurrent ? group.lowestProduct?.priceText : product.priceText}`;
      const status = !isCurrent && !product.available ? this.config.labels.soldOut : '';
      const subline =
        secondaryLabel !== ''
          ? `${secondaryLabel} · ${priceText}`
          : priceText;
      const primaryContent = `<span class="product-layout__format-edition-title">${escapeHTML(primaryLabel)}</span>`;

      return `
        <${tag} class="${stateClasses}" ${attrs} ${isCurrent ? 'aria-current="true"' : ''}>
          ${primaryContent}
          <span class="product-layout__format-edition-meta">${escapeHTML(subline)}</span>
          ${status ? `<span class="product-layout__format-edition-status">${escapeHTML(status)}</span>` : ''}
          ${
            hasMoreEditions
              ? `<span class="product-layout__format-edition-more">${escapeHTML(this.config.labels.moreEditions)}</span>`
              : ''
          }
        </${tag}>
      `;
    }
  }

  if (!customElements.get('product-format-picker')) {
    customElements.define('product-format-picker', ProductFormatPicker);
  }
})();
